import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/Card';
import { Button } from '../../components/Button';
import { useVaultStore, VaultDashboard as Summary } from '../../store/vaultStore';
import { KeyRound, Users, FileKey, ShieldCheck, RefreshCw, ArrowRight } from 'lucide-react';

export function VaultDashboard() {
  const navigate = useNavigate();
  const fetchDashboard = useVaultStore((s) => s.fetchDashboard);
  const [summary, setSummary] = useState<Summary | null>(null);

  useEffect(() => { fetchDashboard().then(setSummary); }, [fetchDashboard]);

  const stats = [
    { label: 'Personal vault', value: summary?.personalVaults ?? 0, icon: KeyRound, tint: 'text-primary bg-primary/10' },
    { label: 'Team vaults', value: summary?.teamVaults ?? 0, icon: Users, tint: 'text-info bg-info/10' },
    { label: 'Credentials', value: summary?.items ?? 0, icon: FileKey, tint: 'text-success bg-success/10' },
    { label: 'Pending approvals', value: summary?.pendingApprovals ?? 0, icon: ShieldCheck, tint: 'text-warning bg-warning/10' },
    { label: 'Rotations due (30d)', value: summary?.upcomingRotations ?? 0, icon: RefreshCw, tint: 'text-destructive bg-destructive/10' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">Vault</h1>
          <p className="text-muted-foreground">Store, share and rotate company credentials securely.</p>
        </div>
        <Button variant="primary" onClick={() => navigate('/vault/vaults')}>
          Open vaults <ArrowRight className="ml-2 size-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <Card key={s.label}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.tint}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{s.label}</p>
                  <h3 className="text-foreground font-display text-metric">{s.value}</h3>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { title: 'Vaults', desc: 'Your personal vault and the team vaults you belong to.', path: '/vault/vaults' },
          { title: 'Access requests', desc: 'Request access to a credential, or approve requests for vaults you manage.', path: '/vault/requests' },
          { title: 'Rotation', desc: 'Track rotation schedules and record completed rotations.', path: '/vault/rotation' },
        ].map((c) => (
          <Card key={c.path} className="hover:border-primary/40 transition-colors cursor-pointer" onClick={() => navigate(c.path)}>
            <CardContent className="p-5">
              <h3 className="text-foreground font-display text-card-title mb-1">{c.title}</h3>
              <p className="text-sm text-muted-foreground">{c.desc}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
